import { algoliasearch } from "algoliasearch"
import type { SearchClient } from "algoliasearch"
import type { StateMapping, UiState } from "instantsearch.js"
import { history } from "instantsearch.js/es/lib/routers"
import type { BrowserHistoryArgs } from "instantsearch.js/es/lib/routers/history"
import { createInsightsMiddleware } from "instantsearch.js/es/middlewares"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { createContext, useEffect, useLayoutEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Configure,
  InstantSearch,
  useClearRefinements,
  useHitsPerPage,
  useInstantSearch,
  usePagination,
  useRange,
  useRefinementList,
  useSearchBox,
  useSortBy,
  useToggleRefinement,
} from "react-instantsearch-core"
import { resolvePath } from "react-router"
import searchInsights from "search-insights"

import { reactQueryCache } from "@sap/utils/algolia"

import queryClient from "@/api/queryClient"
import Router from "@/routes"
import { useAuthStore } from "@/store/auth"

import { createStateMapping } from "./algoliaRouting"

const PRICE_SEARCH_FEATURE_FLAG = "price_sort_filter"
const MATERIAL_FILTER_FEATURE_FLAG = "material_filter"
const LCA_FILTER_FEATURE_FLAG = "lca_filter"

function getPriceAttribute(currencyCode?: string) {
  return currencyCode === "NOK" ? "min_price_nok" : "min_price"
}

interface RouterArgs extends BrowserHistoryArgs<UiState> {
  inPopState?: boolean
}

export default function AlgoliaProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const { user } = useAuthStore((state) => state)
  const lcaFilterEnabled = useFeatureFlagEnabled(LCA_FILTER_FEATURE_FLAG)
  const materialFilterEnabled = useFeatureFlagEnabled(MATERIAL_FILTER_FEATURE_FLAG)
  const priceSearchEnabled = useFeatureFlagEnabled(PRICE_SEARCH_FEATURE_FLAG)
  const priceAttribute = getPriceAttribute(user?.selected_currency_code)

  const indexName = useMemo(() => {
    return `sap_pl_model_${i18n.language}_${import.meta.env.VITE_ALGOLIA_ENV}`
  }, [i18n.language])

  const routing = useMemo(() => {
    return {
      router: history({
        start(onUpdate) {
          const unsub = Router.subscribe((e) => {
            if (e.location.pathname === "/") {
              onUpdate()
            }
          })
          return unsub as unknown as void
        },
        push(url) {
          const path = resolvePath(url)
          const location = Router.state.location.pathname
          if (location === "/") {
            path.pathname = location
            Router.navigate(path)
          }
        },
        getLocation() {
          this.inPopState = false
          return window.location
        },
        writeDelay: 200,
        cleanUrlOnDispose: false,
      } as RouterArgs),
      stateMapping: createStateMapping(
        indexName,
        !!lcaFilterEnabled,
        !!materialFilterEnabled,
        !!priceSearchEnabled,
        priceAttribute
      ) as unknown as StateMapping<UiState, UiState>,
    }
  }, [indexName, lcaFilterEnabled, materialFilterEnabled, priceAttribute, priceSearchEnabled])

  const searchClient = useMemo(() => {
    const algoliaClient = algoliasearch(
      import.meta.env.VITE_ALGOLIA_APP_ID || "",
      user?.algolia_token || "",
      {
        responsesCache: reactQueryCache(queryClient),
      }
    )

    const searchClient = {
      ...algoliaClient,
      search(requests) {
        const shouldSearch = Router.state.location.pathname === "/"
        if (shouldSearch) {
          return algoliaClient.search(requests)
        }
        return Promise.resolve({
          results: Array.isArray(requests)
            ? requests.map(() => ({
                hits: [],
                nbHits: 0,
                nbPages: 0,
                page: 0,
                processingTimeMS: 0,
                hitsPerPage: 0,
                exhaustiveNbHits: false,
                query: "",
                params: "",
              }))
            : [],
        })
      },
      searchForFacetValues: algoliaClient.searchForFacetValues,
    } as SearchClient

    return searchClient
  }, [user?.algolia_token])

  return (
    <InstantSearch
      searchClient={searchClient}
      routing={routing}
      indexName={indexName}
      stalledSearchDelay={0}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <Configure index={indexName} />
      <UserInsightsConnector />
      <FeatureFlaggedFilterCleanup
        lcaFilterEnabled={!!lcaFilterEnabled}
        materialFilterEnabled={!!materialFilterEnabled}
        priceSearchEnabled={!!priceSearchEnabled}
      />
      <VirtualRefinementList />
      <VirtualToggleRefinement />
      <VirtualPagination />
      {priceSearchEnabled && <VirtualRange />}
      <VirtualHitsPerPage />
      <VirtualSearchBox />
      <VirtualSortBy>{children}</VirtualSortBy>
    </InstantSearch>
  )
}

function UserInsightsConnector() {
  const { user } = useAuthStore((state) => state)
  const { addMiddlewares } = useInstantSearch()

  useEffect(() => {}, [])

  useLayoutEffect(() => {
    if (user?.id && user?.algolia_token) {
      searchInsights("init", {
        appId: import.meta.env.VITE_ALGOLIA_APP_ID || "",
        apiKey: user.algolia_token,
      })
      searchInsights("setUserToken", `${user.id}`)

      const middleware = createInsightsMiddleware({
        insightsClient: searchInsights,
      })

      return addMiddlewares(middleware)
    }
  }, [addMiddlewares, user?.id, user?.algolia_token])

  return null
}

function FeatureFlaggedFilterCleanup({
  lcaFilterEnabled,
  materialFilterEnabled,
  priceSearchEnabled,
}: {
  lcaFilterEnabled: boolean
  materialFilterEnabled: boolean
  priceSearchEnabled: boolean
}) {
  useClearDisabledRefinementList({
    attribute: "lca_first_life_rating",
    enabled: lcaFilterEnabled,
  })

  useClearDisabledRefinementList({
    attribute: "material",
    enabled: materialFilterEnabled,
  })

  const { refine: clearPriceRefinements, canRefine: hasPriceRefinements } = useClearRefinements({
    includedAttributes: ["min_price", "min_price_nok"],
  })

  useEffect(() => {
    if (!priceSearchEnabled && hasPriceRefinements) {
      clearPriceRefinements()
    }
  }, [clearPriceRefinements, hasPriceRefinements, priceSearchEnabled])

  return null
}

function useClearDisabledRefinementList({
  attribute,
  enabled,
}: {
  attribute: string
  enabled: boolean
}) {
  const { refine: clearRefinements, canRefine } = useClearRefinements({
    includedAttributes: [attribute],
  })

  useEffect(() => {
    if (!enabled && canRefine) {
      clearRefinements()
    }
  }, [canRefine, clearRefinements, enabled])
}

function VirtualRefinementList() {
  useRefinementList({
    attribute: "category_plural",
    sortBy: ["name:asc"],
    limit: 20,
  })

  useRefinementList({
    attribute: "material",
    sortBy: ["name:asc"],
    limit: 50,
  })

  useRefinementList({
    attribute: "lca_first_life_rating",
    sortBy: ["name:asc"],
    limit: 7,
  })

  return null
}

function VirtualToggleRefinement() {
  useToggleRefinement({
    attribute: "brand_new",
  })
  useToggleRefinement({
    attribute: "outdoor",
  })
  useToggleRefinement({
    attribute: "indoor",
  })
  useToggleRefinement({
    attribute: "stackable",
  })
  useToggleRefinement({
    attribute: "sustainable",
  })
  useToggleRefinement({
    attribute: "satelliet_originals",
  })
  return null
}

function VirtualPagination() {
  usePagination()

  return null
}

function VirtualRange() {
  const { user } = useAuthStore((state) => state)

  useRange({ attribute: getPriceAttribute(user?.selected_currency_code) })

  return null
}

function VirtualHitsPerPage() {
  useHitsPerPage({
    items: [{ label: "16 hits per page", value: 16, default: true }],
  })
  return null
}

function VirtualSearchBox() {
  useSearchBox()

  return null
}

// eslint-disable-next-line react-refresh/only-export-components
export const SortByContext = createContext<{
  refine: (value: string) => void
  currentRefinement: string
  options: { label: string; value: string }[]
}>({
  refine: () => {},
  currentRefinement: "",
  options: [],
})

function VirtualSortBy({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation()
  const popularitySortEnabled = useFeatureFlagEnabled("popularity_sort")
  const priceSearchEnabled = useFeatureFlagEnabled(PRICE_SEARCH_FEATURE_FLAG)

  const defaultIndexName = `sap_pl_model_${i18n.language}_${import.meta.env.VITE_ALGOLIA_ENV}`
  const descNameIndexName = `sap_pl_model_${i18n.language}_sort_name_desc_${
    import.meta.env.VITE_ALGOLIA_ENV
  }`
  const descSalesCountIndexName = `sap_pl_model_${i18n.language}_sort_sales_count_desc_${
    import.meta.env.VITE_ALGOLIA_ENV
  }`
  const descPageViewsIndexName = `sap_pl_model_${i18n.language}_sort_page_views_desc_${
    import.meta.env.VITE_ALGOLIA_ENV
  }`
  const ascPriceIndexName = `sap_pl_model_${i18n.language}_sort_price_asc_${
    import.meta.env.VITE_ALGOLIA_ENV
  }`
  const descPriceIndexName = `sap_pl_model_${i18n.language}_sort_price_desc_${
    import.meta.env.VITE_ALGOLIA_ENV
  }`

  const sortBy = useSortBy({
    items: [
      { label: t("ascName"), value: defaultIndexName },
      {
        label: t("descName"),
        value: descNameIndexName,
      },
      ...(priceSearchEnabled
        ? [
            {
              label: t("ascPrice"),
              value: ascPriceIndexName,
            },
            {
              label: t("descPrice"),
              value: descPriceIndexName,
            },
          ]
        : []),
      ...(popularitySortEnabled
        ? [
            {
              label: t("descSalesCount"),
              value: descSalesCountIndexName,
            },
            {
              label: t("descPageViews"),
              value: descPageViewsIndexName,
            },
          ]
        : []),
    ],
  })

  useEffect(() => {
    if (!sortBy.options.some((option) => option.value === sortBy.currentRefinement)) {
      sortBy.refine(defaultIndexName)
    }
  }, [defaultIndexName, sortBy])

  return <SortByContext.Provider value={sortBy}>{children}</SortByContext.Provider>
}
